import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import AlertToast from '../../components/common/AlertToast';

// Apply branding colors to CSS variables immediately (sidebar, buttons, accents)
function applyBrandingToDOM(b) {
  if (!b) return;
  const root = document.documentElement;
  const setColor = (varName, hex) => {
    if (!hex || !hex.startsWith('#') || hex.length < 7) return;
    root.style.setProperty(varName, hex);
    try {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const bv = parseInt(hex.slice(5, 7), 16);
      root.style.setProperty(`${varName}-rgb`, `${r},${g},${bv}`);
      const dk = v => Math.max(0, v - 35).toString(16).padStart(2, '0');
      root.style.setProperty(`${varName}-dark`, `#${dk(r)}${dk(g)}${dk(bv)}`);
      const lt = v => Math.min(255, v + 40).toString(16).padStart(2, '0');
      root.style.setProperty(`${varName}-light`, `#${lt(r)}${lt(g)}${lt(bv)}`);
    } catch (_) {}
  };
  setColor('--tenant-primary',   b.primaryColor);
  setColor('--tenant-secondary', b.secondaryColor);
  setColor('--tenant-accent',    b.accentColor);
  if (b.primaryColor) {
    // Also override Bootstrap's --bs-primary so badges, buttons, etc. pick it up
    root.style.setProperty('--bs-primary', b.primaryColor);
    root.style.setProperty('--bs-link-color', b.primaryColor);
  }
}

const FIELD_TYPES = [
  { value: 'text',       label: 'Text' },
  { value: 'textarea',   label: 'Textarea' },
  { value: 'number',     label: 'Number' },
  { value: 'email',      label: 'Email' },
  { value: 'phone',      label: 'Phone' },
  { value: 'date',       label: 'Date' },
  { value: 'select',     label: 'Dropdown' },
  { value: 'currency',   label: 'Currency (₹)' },
  { value: 'boolean',    label: 'Yes / No' },
  { value: 'url',        label: 'URL / Link' },
  { value: 'file',       label: 'File Upload' },
  { value: 'image',      label: 'Image Upload' },
];

// ─── Module Fields Manager ────────────────────────────────────────────────────
function ModuleFieldsManager({ moduleKey, moduleName }) {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const blank = { fieldKey: '', label: '', fieldType: 'text', isRequired: false, showInList: true, group: 'General', width: 'col-md-6', order: 99, placeholder: '' };
  const [form, setForm] = useState(blank);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/modules/${moduleKey}/fields`)
      .then(r => setFields(r.data?.records || r.data?.data || []))
      .catch(() => setFields([]))
      .finally(() => setLoading(false));
  }, [moduleKey]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setForm(blank); setEditingField(null); setShowAdd(true); };
  const openEdit = (f) => {
    setForm({ fieldKey: f.fieldKey, label: f.label, fieldType: f.fieldType, isRequired: f.isRequired, showInList: f.showInList, group: f.group, width: f.width, order: f.order, placeholder: f.placeholder || '' });
    setEditingField(f);
    setShowAdd(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.fieldKey || !form.label) { setToast({ show: true, message: 'Field Key and Label are required', type: 'danger' }); return; }
    setSaving(true);
    try {
      if (editingField && !editingField.isSystem) {
        // Custom field — update in place
        await api.put(`/modules/${moduleKey}/fields/${editingField._id}`, form);
        setToast({ show: true, message: 'Field updated!', type: 'success' });
      } else if (editingField && editingField.isSystem) {
        // Platform field — create a tenant override with the same fieldKey
        await api.post(`/modules/${moduleKey}/fields`, form);
        setToast({ show: true, message: 'Field override saved! Your label will be used for this organization.', type: 'success' });
      } else {
        await api.post(`/modules/${moduleKey}/fields`, form);
        setToast({ show: true, message: 'Custom field added!', type: 'success' });
      }
      setShowAdd(false); setEditingField(null); load();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || 'Error saving field', type: 'danger' });
    } finally { setSaving(false); }
  };

  const handleDelete = async (field) => {
    if (field.isSystem) { setToast({ show: true, message: 'Platform default fields cannot be deleted', type: 'warning' }); return; }
    if (!window.confirm(`Delete field "${field.label}"?`)) return;
    try {
      await api.delete(`/modules/${moduleKey}/fields/${field._id}`);
      setToast({ show: true, message: 'Field deleted', type: 'success' });
      load();
    } catch { setToast({ show: true, message: 'Failed to delete', type: 'danger' }); }
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{moduleName}</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>{fields.length} fields configured</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={openAdd}>
          <i className="bi bi-plus-lg me-1"></i>Add Custom Field
        </button>
      </div>

      {/* Add / Edit Form */}
      {showAdd && (
        <div className="card mb-3" style={{ border: '2px solid #e0e7ff', borderRadius: 12 }}>
          <div className="card-header d-flex align-items-center justify-content-between" style={{ background: '#f5f3ff', borderBottom: '1px solid #e0e7ff' }}>
            <span style={{ fontWeight: 600, fontSize: 13 }}>
              <i className="bi bi-plus-circle me-2 text-primary"></i>
              {editingField?.isSystem
                ? <><i className="bi bi-shield-check me-1 text-success"></i>Override Platform Field: {editingField.label}</>
                : editingField
                  ? `Edit: ${editingField.label}`
                  : 'New Custom Field'}
            </span>
            <button className="btn-close btn-sm" onClick={() => { setShowAdd(false); setEditingField(null); }}></button>
          </div>
          <div className="card-body">
            {editingField?.isSystem && (
              <div className="alert alert-warning d-flex gap-2 align-items-start mb-3" style={{ fontSize: 12, borderRadius: 8 }}>
                <i className="bi bi-shield-exclamation mt-1" style={{ flexShrink: 0 }}></i>
                <div>
                  This is a <strong>platform default field</strong>. Your changes will create an organization-level override — only your organization will see the new label. The platform default is not modified.
                </div>
              </div>
            )}
            <form onSubmit={handleSave}>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">Field Key <span className="text-danger">*</span></label>
                  <input className="form-control" placeholder="e.g. branch_code" value={form.fieldKey}
                    onChange={e => setForm(f => ({ ...f, fieldKey: e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') }))}
                    readOnly={!!editingField} />
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>Lowercase letters, numbers, underscores only</div>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Display Label <span className="text-danger">*</span></label>
                  <input className="form-control" placeholder="e.g. Branch Code" value={form.label}
                    onChange={e => setForm(f => ({ ...f, label: e.target.value }))} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Field Type</label>
                  <select className="form-select" value={form.fieldType} onChange={e => setForm(f => ({ ...f, fieldType: e.target.value }))}>
                    {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Group / Section</label>
                  <input className="form-control" placeholder="e.g. Basic Info" value={form.group}
                    onChange={e => setForm(f => ({ ...f, group: e.target.value }))} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Column Width</label>
                  <select className="form-select" value={form.width} onChange={e => setForm(f => ({ ...f, width: e.target.value }))}>
                    <option value="col-md-3">25% (Quarter)</option>
                    <option value="col-md-4">33% (Third)</option>
                    <option value="col-md-6">50% (Half)</option>
                    <option value="col-md-8">66% (Two-thirds)</option>
                    <option value="col-md-12">100% (Full)</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Order</label>
                  <input type="number" className="form-control" value={form.order}
                    onChange={e => setForm(f => ({ ...f, order: Number(e.target.value) }))} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Placeholder</label>
                  <input className="form-control" value={form.placeholder}
                    onChange={e => setForm(f => ({ ...f, placeholder: e.target.value }))} />
                </div>
                <div className="col-12 d-flex gap-4">
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="isRequired" checked={form.isRequired}
                      onChange={e => setForm(f => ({ ...f, isRequired: e.target.checked }))} />
                    <label className="form-check-label" htmlFor="isRequired" style={{ fontSize: 13 }}>Required field</label>
                  </div>
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="showInList" checked={form.showInList}
                      onChange={e => setForm(f => ({ ...f, showInList: e.target.checked }))} />
                    <label className="form-check-label" htmlFor="showInList" style={{ fontSize: 13 }}>Show in list/table</label>
                  </div>
                </div>
              </div>
              <div className="d-flex gap-2 justify-content-end mt-3">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setShowAdd(false); setEditingField(null); }}>Cancel</button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                  {saving && <span className="spinner-border spinner-border-sm me-1"></span>}
                  {editingField ? 'Update Field' : 'Add Field'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Fields table */}
      {loading ? (
        <div className="text-center py-4"><span className="spinner-border spinner-border-sm text-primary"></span></div>
      ) : fields.length === 0 ? (
        <div className="text-center py-4 text-muted" style={{ fontSize: 13 }}>No fields defined. Add a custom field above.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover mb-0" style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th>#</th><th>Label</th><th>Field Key</th><th>Type</th><th>Group</th><th>Required</th><th>In List</th><th>Source</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {fields.map((f, i) => (
                <tr key={f._id || f.fieldKey}>
                  <td style={{ color: '#94a3b8' }}>{f.order ?? i + 1}</td>
                  <td style={{ fontWeight: 600 }}>{f.label}</td>
                  <td><code style={{ fontSize: 11, background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>{f.fieldKey}</code></td>
                  <td><span className="badge" style={{ background: '#e0f2fe', color: '#0369a1', fontSize: 11 }}>{f.fieldType}</span></td>
                  <td style={{ color: '#64748b' }}>{f.group || '—'}</td>
                  <td>{f.isRequired ? <i className="bi bi-check-circle-fill text-success"></i> : <i className="bi bi-dash text-muted"></i>}</td>
                  <td>{f.showInList !== false ? <i className="bi bi-check-circle-fill text-success"></i> : <i className="bi bi-dash text-muted"></i>}</td>
                  <td>
                    {f.isSystem
                      ? <span className="badge" style={{ background: '#f0fdf4', color: '#16a34a', fontSize: 10 }}>Platform</span>
                      : <span className="badge" style={{ background: '#faf5ff', color: '#7c3aed', fontSize: 10 }}>Custom</span>}
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      <button
                        className="btn btn-outline-primary btn-sm"
                        style={{ padding: '3px 8px' }}
                        onClick={() => openEdit(f)}
                        title={f.isSystem ? 'Override this field label for your organization' : 'Edit field'}
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button className="btn btn-outline-danger btn-sm" style={{ padding: '3px 8px' }} onClick={() => handleDelete(f)}
                        disabled={f.isSystem} title={f.isSystem ? 'Platform fields cannot be deleted' : 'Delete field'}>
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <AlertToast {...toast} onClose={() => setToast(t => ({ ...t, show: false }))} />
    </div>
  );
}

// ─── Main Settings Page ───────────────────────────────────────────────────────
export default function Settings() {
  const { tenant, activeModules, patchTenant, user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('general');
  const [form, setForm] = useState({ name: '', contactEmail: '', contactPhone: '', website: '', numEmployees: '', industry: '', orgType: '', address: { street: '', city: '', state: '', country: '', pincode: '' } });
  // Initialise from the live tenant branding so the colour inputs show the
  // real current colours on first render (avoids a "flash of indigo").
  const [branding, setBranding] = useState(() => tenant?.branding || { primaryColor: '#6366f1' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [selectedModule, setSelectedModule] = useState('');
  const [copiedKey, setCopiedKey] = useState('');
  const DEFAULT_LEAVE_POLICY = [
    { key: 'casual',   label: 'Casual Leave',    maxDays: 12, color: '#3b82f6', isHours: false, requiresPostProbation: false },
    { key: 'sick',     label: 'Sick Leave',       maxDays: 10, color: '#ef4444', isHours: false, requiresPostProbation: false },
    { key: 'earned',   label: 'Earned Leave',     maxDays: 15, color: '#22c55e', isHours: false, requiresPostProbation: true  },
    { key: 'maternity',label: 'Maternity Leave',  maxDays: 90, color: '#a855f7', isHours: false, requiresPostProbation: false },
    { key: 'paternity',label: 'Paternity Leave',  maxDays: 5,  color: '#6366f1', isHours: false, requiresPostProbation: false },
    { key: 'comp_off', label: 'Compensatory Off', maxDays: 5,  color: '#f59e0b', isHours: false, requiresPostProbation: false },
  ];
  const [leavePolicy, setLeavePolicy] = useState(DEFAULT_LEAVE_POLICY);
  const [leaveSaving, setLeaveSaving] = useState(false);
  const [domainInfo, setDomainInfo] = useState({ subdomain: '', customDomain: '', customDomainVerified: false, customDomainVerifiedAt: null });
  const [domainSaving, setDomainSaving] = useState(false);
  const [domainVerifying, setDomainVerifying] = useState(false);
  // Course-Record feature toggles. Default all to enabled so existing
  // tenants keep working — the only "off by default" case would be a
  // tenant that explicitly opted out previously.
  const [featureToggles, setFeatureToggles] = useState({
    courseRecordEmailNotifications: true,
  });
  const [togglesSaving, setTogglesSaving] = useState(false);

  const slug = tenant?.slug || '';

  // Compute platform root domain (e.g. yourapp.com) from current hostname
  const platformDomain = (() => {
    const h = window.location.hostname;
    if (h === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(h)) return 'yourapp.com';
    const parts = h.split('.');
    return parts.length >= 2 ? parts.slice(-2).join('.') : h;
  })();
  const isLocalhost = window.location.hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(window.location.hostname);

  const subdomainUrl = `https://${domainInfo.subdomain || slug}.${platformDomain}/login`;
  const fallbackUrl  = `${window.location.origin}/login?org=${slug}`;
  // Deprecated alias kept for email links
  const loginLink = fallbackUrl;

  useEffect(() => {
    api.get('/settings').then(r => {
      const d = r.data?.data || {};
      setForm({ name: d.name || '', contactEmail: d.contactEmail || '', contactPhone: d.contactPhone || '', website: d.website || '', numEmployees: d.numEmployees || '', industry: d.industry || '', orgType: d.orgType || '', address: d.address || { street: '', city: '', state: '', country: '', pincode: '' } });
      if (d.branding) {
        setBranding(d.branding);
        // Apply the saved branding immediately on load
        applyBrandingToDOM(d.branding);
        localStorage.setItem('tenant_branding', JSON.stringify(d.branding));
      }
      if (d.settings?.leavePolicy?.length) setLeavePolicy(d.settings.leavePolicy);
      if (d.settings?.featureToggles) setFeatureToggles(d.settings.featureToggles);
    }).finally(() => setLoading(false));

    api.get('/settings/domain').then(r => {
      const d = r.data?.data || {};
      setDomainInfo({
        subdomain: d.subdomain || '',
        customDomain: d.customDomain || '',
        customDomainVerified: d.customDomainVerified || false,
        customDomainVerifiedAt: d.customDomainVerifiedAt || null,
      });
    }).catch(() => {});
  }, []);

  // Pre-select first non-core module for fields tab
  useEffect(() => {
    if (activeTab === 'fields' && !selectedModule && activeModules?.length > 0) {
      const first = activeModules.find(m => !['dashboard','users','roles','settings','notifications'].includes(m.key));
      if (first) setSelectedModule(first.key);
    }
  }, [activeTab, activeModules, selectedModule]);

  // LIVE THEME PREVIEW — every edit to branding (preset click, colour picker,
  // logo upload) propagates into AuthContext immediately. This triggers
  // TenantLayout's branding effect to re-inject the full <style id="tenant-theme">
  // block, so buttons / tabs / pills / badges / progress bars re-theme on the spot.
  // The Save Branding button still needs to be clicked to persist to the backend.
  useEffect(() => {
    if (!patchTenant) return;
    // Skip until we've loaded the real branding from /settings so we don't
    // briefly overwrite server state with the default indigo on first mount.
    if (!branding || (!branding.primaryColor && !branding.logo)) return;
    applyBrandingToDOM(branding);
    patchTenant({ branding: { ...(tenant?.branding || {}), ...branding } });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branding]);

  const copyLink = (text, key) => {
    navigator.clipboard.writeText(text).then(() => { setCopiedKey(key); setTimeout(() => setCopiedKey(''), 2000); });
  };

  const saveGeneral = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.put('/settings', form);
      // Reflect changes in AuthContext so receipts / certificates / sidebar
      // pick up the new org name, address, contact info etc. immediately.
      if (patchTenant) patchTenant({
        name: form.name,
        contactEmail: form.contactEmail,
        contactPhone: form.contactPhone,
        website: form.website,
        address: form.address,
      });
      setToast({ show: true, message: 'Settings saved!', type: 'success' });
    } catch { setToast({ show: true, message: 'Failed to save', type: 'danger' }); }
    finally { setSaving(false); }
  };

  const saveBranding = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.put('/settings/branding', branding);
      // Apply to DOM immediately so sidebar/buttons update without refresh
      applyBrandingToDOM(branding);
      localStorage.setItem('tenant_branding', JSON.stringify(branding));
      // Push the new branding into AuthContext so TenantLayout's theme-injection
      // effect re-runs (applies buttons / tabs / badges across the whole app)
      // AND components that read tenant.branding.logo (receipts, certificates
      // etc.) pick up the new logo without a page reload.
      if (patchTenant) patchTenant({ branding: { ...(tenant?.branding || {}), ...branding } });
      setToast({ show: true, message: 'Branding applied and saved!', type: 'success' });
    } catch { setToast({ show: true, message: 'Failed to save branding', type: 'danger' }); }
    finally { setSaving(false); }
  };

  const saveDomain = async (e) => {
    e.preventDefault(); setDomainSaving(true);
    try {
      await api.put('/settings/domain', { subdomain: domainInfo.subdomain, customDomain: domainInfo.customDomain });
      setToast({ show: true, message: 'Domain settings saved!', type: 'success' });
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || 'Failed to save', type: 'danger' });
    } finally { setDomainSaving(false); }
  };

  const saveLeavePolicy = async () => {
    setLeaveSaving(true);
    try {
      await api.put('/settings/leave-policy', { leavePolicy });
      setToast({ show: true, message: 'Leave policy saved!', type: 'success' });
    } catch { setToast({ show: true, message: 'Failed to save leave policy', type: 'danger' }); }
    finally { setLeaveSaving(false); }
  };

  const saveFeatureToggles = async () => {
    setTogglesSaving(true);
    try {
      await api.put('/settings/feature-toggles', { toggles: featureToggles });
      setToast({ show: true, message: 'Notification settings saved!', type: 'success' });
    } catch { setToast({ show: true, message: 'Failed to save notification settings', type: 'danger' }); }
    finally { setTogglesSaving(false); }
  };

  const addLeaveType = () => setLeavePolicy(p => [...p, { key: '', label: '', maxDays: 10, color: '#6366f1', isHours: false, requiresPostProbation: false }]);
  const removeLeaveType = (i) => setLeavePolicy(p => p.filter((_, idx) => idx !== i));
  const updateLeaveType = (i, field, value) => setLeavePolicy(p => p.map((lt, idx) => idx === i ? { ...lt, [field]: value } : lt));

  const verifyDomain = async () => {
    setDomainVerifying(true);
    try {
      const r = await api.post('/settings/domain/verify');
      const verified = r.data?.data?.verified;
      setDomainInfo(d => ({ ...d, customDomainVerified: verified }));
      setToast({ show: true, message: verified ? 'Domain verified!' : 'DNS not propagated yet. Wait 24-48h and retry.', type: verified ? 'success' : 'warning' });
    } catch (err) {
      setToast({ show: true, message: 'Verification failed. Check your DNS.', type: 'danger' });
    } finally { setDomainVerifying(false); }
  };

  if (loading) return <div className="page-loader"><div className="spinner-border text-primary"></div></div>;

  const moduleOptions = (activeModules || []).filter(m => !['dashboard','users','roles','settings','notifications'].includes(m.key));
  const selectedModuleInfo = moduleOptions.find(m => m.key === selectedModule);

  const TABS = [
    { key: 'general',       icon: 'bi-building',                label: 'General' },
    { key: 'branding',      icon: 'bi-palette',                 label: 'Branding' },
    { key: 'fields',        icon: 'bi-layout-three-columns',    label: 'Module Fields' },
    { key: 'leave',         icon: 'bi-calendar-check',          label: 'Leave Policy' },
    { key: 'notifications', icon: 'bi-bell',                    label: 'Notifications' },
    { key: 'access',        icon: 'bi-link-45deg',              label: 'Login & Access' },
    { key: 'domain',        icon: 'bi-globe2',                  label: 'Custom Domain' },
  ];

  return (
    <div>
      <PageHeader title="Organization Settings" icon="bi-gear" />

      {/* Tabs */}
      <div className="card mb-4">
        <div className="card-body p-0">
          <div className="d-flex border-bottom" style={{ overflowX: 'auto' }}>
            {TABS.map(tab => (
              <button key={tab.key} type="button"
                onClick={() => setActiveTab(tab.key)}
                style={{
                  border: 'none', background: 'none', padding: '14px 20px',
                  fontWeight: 600, fontSize: 13,
                  borderBottom: activeTab === tab.key ? '2px solid var(--tenant-primary, #6366f1)' : '2px solid transparent',
                  color: activeTab === tab.key ? 'var(--tenant-primary, #6366f1)' : '#64748b',
                  whiteSpace: 'nowrap', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7,
                }}
              >
                <i className={`bi ${tab.icon}`}></i>{tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* General Tab */}
      {activeTab === 'general' && (
        <div className="card">
          <div className="card-header">General Information</div>
          <div className="card-body">
            <form onSubmit={saveGeneral}>
              <div className="row g-3">
                <div className="col-md-6"><label className="form-label">Organization Name</label><input className="form-control" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
                <div className="col-md-6"><label className="form-label">Contact Email</label><input type="email" className="form-control" value={form.contactEmail} onChange={e => setForm(f => ({ ...f, contactEmail: e.target.value }))} /></div>
                <div className="col-md-4"><label className="form-label">Phone</label><input className="form-control" value={form.contactPhone} onChange={e => setForm(f => ({ ...f, contactPhone: e.target.value }))} /></div>
                <div className="col-md-8"><label className="form-label">Website</label><input type="url" className="form-control" value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} /></div>
                <div className="col-md-4"><label className="form-label">No. of Employees / Staff</label>
                  <select className="form-select" value={form.numEmployees || ''} onChange={e => setForm(f => ({ ...f, numEmployees: e.target.value }))}>
                    <option value="">Select range...</option>
                    {['1-10', '11-25', '26-50', '51-100', '101-250', '251-500', '500+'].map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <div className="col-md-4"><label className="form-label">Industry / Sector</label>
                  <select className="form-select" value={form.industry || ''} onChange={e => setForm(f => ({ ...f, industry: e.target.value }))}>
                    <option value="">Select...</option>
                    {['Education - School', 'Education - College', 'Training Institute', 'Healthcare', 'Manufacturing', 'Retail', 'Software / IT', 'Finance', 'Other'].map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <div className="col-md-4"><label className="form-label">Type of Organization</label>
                  <select className="form-select" value={form.orgType || ''} onChange={e => setForm(f => ({ ...f, orgType: e.target.value }))}>
                    <option value="">Select...</option>
                    {['Government', 'Private', 'Aided', 'Autonomous', 'Non-Profit / Trust', 'Partnership', 'Other'].map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <div className="col-md-5"><label className="form-label">City</label><input className="form-control" value={form.address?.city || ''} onChange={e => setForm(f => ({ ...f, address: { ...f.address, city: e.target.value } }))} /></div>
                <div className="col-md-4"><label className="form-label">State</label><input className="form-control" value={form.address?.state || ''} onChange={e => setForm(f => ({ ...f, address: { ...f.address, state: e.target.value } }))} /></div>
                <div className="col-md-3"><label className="form-label">Country</label><input className="form-control" value={form.address?.country || ''} onChange={e => setForm(f => ({ ...f, address: { ...f.address, country: e.target.value } }))} /></div>
              </div>
              <div className="d-flex justify-content-end mt-3">
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                  {saving && <span className="spinner-border spinner-border-sm me-1"></span>}Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Branding Tab */}
      {activeTab === 'branding' && (
        <div className="row g-3">
          <div className="col-lg-7">
            <div className="card">
              <div className="card-header fw-semibold">Brand Customization</div>
              <div className="card-body">
                <form onSubmit={saveBranding}>
                  {/* Logo */}
                  <div className="mb-3">
                    <label className="form-label fw-medium">Organization Logo</label>
                    <div className="d-flex align-items-center gap-3">
                      {branding.logo && (
                        <img src={branding.logo} alt="logo" style={{ height: 60, maxWidth: 120, objectFit: 'contain', border: '1px solid #e2e8f0', borderRadius: 8, padding: 6, background: '#f8fafc' }} />
                      )}
                      <div>
                        <input type="file" className="form-control form-control-sm" accept="image/*" style={{ maxWidth: 280 }}
                          onChange={e => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const reader = new FileReader();
                            reader.onload = ev => setBranding(b => ({ ...b, logo: ev.target.result }));
                            reader.readAsDataURL(file);
                          }} />
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>PNG, JPG, SVG — max 2MB recommended</div>
                      </div>
                    </div>
                  </div>

                  {/* Preset Themes */}
                  <div className="mb-3">
                    <label className="form-label fw-medium">Quick Themes</label>
                    <div className="d-flex gap-2 flex-wrap">
                      {[
                        { name: 'Indigo', primary: '#6366f1', secondary: '#8b5cf6', accent: '#06b6d4' },
                        { name: 'Ocean', primary: '#0ea5e9', secondary: '#06b6d4', accent: '#10b981' },
                        { name: 'Emerald', primary: '#10b981', secondary: '#059669', accent: '#f59e0b' },
                        { name: 'Rose', primary: '#f43f5e', secondary: '#e11d48', accent: '#f97316' },
                        { name: 'Slate', primary: '#475569', secondary: '#334155', accent: '#6366f1' },
                      ].map(t => (
                        <button key={t.name} type="button"
                          onClick={() => setBranding(b => ({ ...b, primaryColor: t.primary, secondaryColor: t.secondary, accentColor: t.accent }))}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 500 }}>
                          <div style={{ display: 'flex', gap: 2 }}>
                            <div style={{ width: 10, height: 10, borderRadius: 2, background: t.primary }} />
                            <div style={{ width: 10, height: 10, borderRadius: 2, background: t.secondary }} />
                            <div style={{ width: 10, height: 10, borderRadius: 2, background: t.accent }} />
                          </div>
                          {t.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Colors */}
                  {[
                    { key: 'primaryColor', label: 'Primary Color', placeholder: '#6366f1' },
                    { key: 'secondaryColor', label: 'Secondary Color', placeholder: '#8b5cf6' },
                    { key: 'accentColor', label: 'Accent Color', placeholder: '#06b6d4' },
                  ].map(c => (
                    <div className="mb-3" key={c.key}>
                      <label className="form-label">{c.label}</label>
                      <div className="d-flex gap-2">
                        <input type="color" className="form-control form-control-color" value={branding[c.key] || c.placeholder} onChange={e => setBranding(b => ({ ...b, [c.key]: e.target.value }))} style={{ width: 52, padding: 4 }} />
                        <input type="text" className="form-control" value={branding[c.key] || ''} onChange={e => setBranding(b => ({ ...b, [c.key]: e.target.value }))} placeholder={c.placeholder} />
                      </div>
                    </div>
                  ))}

                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving && <span className="spinner-border spinner-border-sm me-1"></span>}Save Branding
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* Preview Card */}
          <div className="col-lg-5">
            <div className="card" style={{ position: 'sticky', top: 20 }}>
              <div className="card-header fw-semibold">Live Preview</div>
              <div className="card-body p-0" style={{ overflow: 'hidden', borderRadius: '0 0 8px 8px' }}>
                <div style={{ display: 'flex', height: 280 }}>
                  {/* Mini Sidebar */}
                  <div style={{ width: 120, background: branding.primaryColor || '#6366f1', padding: '16px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                      {branding.logo
                        ? <img src={branding.logo} alt="" style={{ height: 24, maxWidth: 32, objectFit: 'contain', borderRadius: 4 }} />
                        : <div style={{ width: 24, height: 24, borderRadius: 6, background: 'rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}><i className="bi bi-buildings-fill" style={{ color: '#fff' }} /></div>
                      }
                      <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.9)', fontWeight: 700, lineHeight: 1.2 }}>{form.name?.slice(0, 12) || 'Organization'}</div>
                    </div>
                    {['Dashboard', 'Students', 'Attendance', 'Exams', 'Settings'].map((m, i) => (
                      <div key={m} style={{ padding: '5px 8px', borderRadius: 6, background: i === 0 ? 'rgba(255,255,255,0.2)' : 'transparent', fontSize: 9, color: 'rgba(255,255,255,0.85)', display: 'flex', gap: 5 }}>
                        <i className="bi bi-circle-fill" style={{ fontSize: 5, marginTop: 3 }} />{m}
                      </div>
                    ))}
                  </div>
                  {/* Mini Content */}
                  <div style={{ flex: 1, background: '#f8fafc', padding: 14 }}>
                    <div style={{ height: 8, background: branding.accentColor || '#06b6d4', borderRadius: 4, width: '60%', marginBottom: 10 }} />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                      {[branding.primaryColor, branding.secondaryColor].map((c, i) => (
                        <div key={i} style={{ background: `${c || '#6366f1'}20`, border: `1px solid ${c || '#6366f1'}30`, borderRadius: 8, padding: '10px 8px' }}>
                          <div style={{ height: 6, background: c || '#6366f1', borderRadius: 3, width: '70%', marginBottom: 5 }} />
                          <div style={{ height: 4, background: '#e2e8f0', borderRadius: 2, width: '90%' }} />
                        </div>
                      ))}
                    </div>
                    <div style={{ height: 4, background: '#e2e8f0', borderRadius: 2, marginBottom: 5 }} />
                    <div style={{ height: 4, background: '#e2e8f0', borderRadius: 2, width: '80%' }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Module Fields Tab */}
      {activeTab === 'fields' && (
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
            <span><i className="bi bi-layout-three-columns me-2"></i>Module Fields</span>
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto', minWidth: 220 }}
              value={selectedModule}
              onChange={e => setSelectedModule(e.target.value)}
            >
              <option value="">— Select a module —</option>
              {moduleOptions.map(m => (
                <option key={m.key} value={m.key}>{m.name}</option>
              ))}
            </select>
          </div>
          <div className="card-body">
            <div className="alert alert-info d-flex gap-2 align-items-start mb-3" style={{ fontSize: 12.5, borderRadius: 9 }}>
              <i className="bi bi-info-circle-fill mt-1" style={{ flexShrink: 0 }}></i>
              <div>
                <strong>Platform fields</strong> (green badge) are set by the Super Admin — click the edit <i className="bi bi-pencil"></i> button to rename them for your organization.
                <strong className="ms-1">Custom fields</strong> (purple badge) are added by you and visible only in your organization.
                Platform fields cannot be deleted, but you can rename them or hide them from forms.
              </div>
            </div>
            {selectedModule
              ? <ModuleFieldsManager key={selectedModule} moduleKey={selectedModule} moduleName={selectedModuleInfo?.name || selectedModule} />
              : <div className="text-center py-5 text-muted" style={{ fontSize: 13 }}>Select a module from the dropdown above to manage its fields.</div>
            }
          </div>
        </div>
      )}

      {/* Leave Policy Tab */}
      {activeTab === 'leave' && (
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between">
            <span><i className="bi bi-calendar-check me-2"></i>Leave Policy</span>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-secondary btn-sm" onClick={addLeaveType}>
                <i className="bi bi-plus-lg me-1"></i>Add Type
              </button>
              <button className="btn btn-primary btn-sm" onClick={saveLeavePolicy} disabled={leaveSaving}>
                {leaveSaving ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving…</> : <><i className="bi bi-check-lg me-1"></i>Save Policy</>}
              </button>
            </div>
          </div>
          <div className="card-body">
            <div className="alert alert-info d-flex gap-2 align-items-start mb-3" style={{ fontSize: 12.5, borderRadius: 9 }}>
              <i className="bi bi-info-circle-fill mt-1" style={{ flexShrink: 0 }}></i>
              <div>Configure leave types, their annual limits, and rules. These settings apply to all employees in your organization.</div>
            </div>
            <div className="table-responsive">
              <table className="table table-hover align-middle" style={{ fontSize: 13 }}>
                <thead className="table-light">
                  <tr>
                    <th>Key</th>
                    <th>Label</th>
                    <th style={{ width: 90 }}>Max Days</th>
                    <th style={{ width: 80 }}>Color</th>
                    <th style={{ width: 90 }}>In Hours?</th>
                    <th style={{ width: 130 }}>Post-Probation Only?</th>
                    <th style={{ width: 50 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {leavePolicy.map((lt, i) => (
                    <tr key={i}>
                      <td>
                        <input
                          className="form-control form-control-sm"
                          placeholder="e.g. casual"
                          value={lt.key}
                          onChange={e => updateLeaveType(i, 'key', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                        />
                      </td>
                      <td>
                        <input
                          className="form-control form-control-sm"
                          placeholder="e.g. Casual Leave"
                          value={lt.label}
                          onChange={e => updateLeaveType(i, 'label', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          min={0}
                          value={lt.maxDays}
                          onChange={e => updateLeaveType(i, 'maxDays', Number(e.target.value))}
                        />
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <input
                            type="color"
                            className="form-control form-control-color form-control-sm p-0"
                            style={{ width: 36, height: 30, cursor: 'pointer' }}
                            value={lt.color || '#6366f1'}
                            onChange={e => updateLeaveType(i, 'color', e.target.value)}
                          />
                          <span className="badge rounded-pill" style={{ background: lt.color || '#6366f1', fontSize: 10 }}>{lt.key || '—'}</span>
                        </div>
                      </td>
                      <td className="text-center">
                        <div className="form-check form-switch d-flex justify-content-center mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={!!lt.isHours}
                            onChange={e => updateLeaveType(i, 'isHours', e.target.checked)}
                          />
                        </div>
                      </td>
                      <td className="text-center">
                        <div className="form-check form-switch d-flex justify-content-center mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={!!lt.requiresPostProbation}
                            onChange={e => updateLeaveType(i, 'requiresPostProbation', e.target.checked)}
                          />
                        </div>
                      </td>
                      <td>
                        <button
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => removeLeaveType(i)}
                          title="Remove"
                        ><i className="bi bi-trash"></i></button>
                      </td>
                    </tr>
                  ))}
                  {leavePolicy.length === 0 && (
                    <tr><td colSpan={7} className="text-center text-muted py-4">No leave types configured. Click "Add Type" to begin.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Notifications Tab — course-record email toggles (and other
          future feature flags). Admin-only setting. */}
      {activeTab === 'notifications' && (
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between">
            <span><i className="bi bi-bell me-2"></i>Notification Settings</span>
            <button className="btn btn-primary btn-sm" onClick={saveFeatureToggles} disabled={togglesSaving}>
              {togglesSaving
                ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving…</>
                : <><i className="bi bi-check-lg me-1"></i>Save Settings</>}
            </button>
          </div>
          <div className="card-body">
            <p className="text-muted small mb-3">
              Control which automated emails are sent to your students and staff.
              All toggles default to <strong>Enabled</strong>; disable to opt out.
            </p>

            {/* Course-record email notifications */}
            <div className="border rounded p-3 mb-3 d-flex align-items-start gap-3">
              <div className="form-check form-switch m-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="toggle-courseRecordEmails"
                  checked={featureToggles.courseRecordEmailNotifications !== false}
                  onChange={e => setFeatureToggles(t => ({ ...t, courseRecordEmailNotifications: e.target.checked }))}
                />
              </div>
              <div className="flex-grow-1">
                <label htmlFor="toggle-courseRecordEmails" className="fw-semibold d-block mb-1" style={{ cursor: 'pointer' }}>
                  <i className="bi bi-envelope-paper me-2 text-primary"></i>Course Record Notifications
                </label>
                <p className="text-muted small mb-0">
                  When a faculty marks topics as completed in a Course Record, the
                  present students of that batch receive a "Today's Class Update" email
                  listing the topics covered. Students marked absent on that day's
                  attendance are skipped. You can always re-send or skip from the
                  Course Records page.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Login & Access Tab */}
      {activeTab === 'access' && (
        <div className="row g-3">
          <div className="col-lg-8">

            {/* ── 1. Subdomain Login URL (Primary / Recommended) ──────────── */}
            <div className="card mb-3" style={{ border: '2px solid #bbf7d0' }}>
              <div className="card-header d-flex align-items-center justify-content-between" style={{ background: '#f0fdf4', borderBottom: '1px solid #bbf7d0' }}>
                <span className="fw-semibold" style={{ color: '#15803d' }}>
                  <i className="bi bi-link-45deg me-2"></i>Subdomain Login URL
                </span>
                <span className="badge" style={{ background: '#22c55e', fontSize: 11, padding: '4px 10px' }}>
                  <i className="bi bi-check-circle-fill me-1"></i>Recommended for Sharing
                </span>
              </div>
              <div className="card-body">
                {isLocalhost && (
                  <div className="alert alert-warning d-flex gap-2 align-items-start py-2 mb-3" style={{ fontSize: 12 }}>
                    <i className="bi bi-exclamation-triangle-fill mt-1" style={{ flexShrink: 0 }}></i>
                    <span>You're on <strong>localhost</strong> — subdomain URLs only work in production. The URL below shows your production format.</span>
                  </div>
                )}
                <p style={{ fontSize: 13, color: '#374151', marginBottom: 10 }}>
                  Share this clean, professional URL with your clients. It uses your organization's subdomain — no internal parameters are exposed.
                </p>
                <div className="input-group mb-3">
                  <input type="text" className="form-control"
                    value={subdomainUrl} readOnly
                    style={{ fontFamily: 'monospace', fontSize: 12.5, background: '#f0fdf4', fontWeight: 600, color: '#15803d' }} />
                  <button className="btn btn-success" onClick={() => copyLink(subdomainUrl, 'subdomain')} style={{ minWidth: 90 }}>
                    {copiedKey === 'subdomain'
                      ? <><i className="bi bi-check-lg me-1"></i>Copied!</>
                      : <><i className="bi bi-clipboard me-1"></i>Copy</>}
                  </button>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                  <a href={subdomainUrl} target="_blank" rel="noreferrer" className="btn btn-outline-success btn-sm">
                    <i className="bi bi-box-arrow-up-right me-1"></i>Preview
                  </a>
                  <a href={`mailto:?subject=Login to ${form.name || 'your workspace'}&body=Hi,%0A%0APlease use the following link to log in to ${form.name || 'your workspace'}:%0A%0A${subdomainUrl}%0A%0AThis is your personalized login link — no extra details required.%0A%0ARegards`}
                    className="btn btn-outline-secondary btn-sm">
                    <i className="bi bi-envelope me-1"></i>Send via Email
                  </a>
                </div>
              </div>
            </div>

            {/* ── 2. Internal Slug ──────────────────────────────────────────── */}
            <div className="card mb-3" style={{ border: '1.5px solid #e2e8f0' }}>
              <div className="card-header d-flex align-items-center justify-content-between" style={{ background: '#f8fafc' }}>
                <span className="fw-semibold">
                  <i className="bi bi-fingerprint me-2 text-secondary"></i>Internal Identifier (Slug)
                </span>
                <span className="badge bg-secondary" style={{ fontSize: 11, padding: '4px 10px' }}>
                  <i className="bi bi-exclamation-circle me-1"></i>Internal Use
                </span>
              </div>
              <div className="card-body">
                <p style={{ fontSize: 13, color: '#64748b', marginBottom: 10 }}>
                  This is your organization's unique internal identifier. It is used by the system to route requests.
                  <strong className="ms-1 text-warning">Not recommended for sharing with clients.</strong>
                </p>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <code style={{ fontSize: 15, fontWeight: 700, background: '#ede9fe', color: '#6366f1', padding: '6px 16px', borderRadius: 8, border: '1px solid #c4b5fd', letterSpacing: '0.5px' }}>
                    {slug || '—'}
                  </code>
                  <button className="btn btn-outline-secondary btn-sm" onClick={() => copyLink(slug, 'slug')}>
                    {copiedKey === 'slug' ? <><i className="bi bi-check-lg me-1"></i>Copied</> : <><i className="bi bi-clipboard me-1"></i>Copy</>}
                  </button>
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>
                  <i className="bi bi-info-circle me-1"></i>
                  Your slug is permanent. Contact support if you need to change it.
                </div>
              </div>
            </div>

            {/* ── 3. Fallback Login URL ────────────────────────────────────── */}
            <div className="card mb-3" style={{ border: '1.5px dashed #fcd34d' }}>
              <div className="card-header d-flex align-items-center justify-content-between" style={{ background: '#fffbeb' }}>
                <span className="fw-semibold" style={{ color: '#92400e' }}>
                  <i className="bi bi-arrow-return-right me-2"></i>Fallback Login URL
                </span>
                <span className="badge bg-warning text-dark" style={{ fontSize: 11, padding: '4px 10px' }}>
                  <i className="bi bi-exclamation-triangle me-1"></i>Use Only if Subdomain Unavailable
                </span>
              </div>
              <div className="card-body">
                <p style={{ fontSize: 13, color: '#78350f', marginBottom: 10 }}>
                  Use this URL only if the subdomain is not configured yet or not working. Share it with your users as a direct login link for your organization.
                </p>
                <div className="input-group mb-3">
                  <input type="text" className="form-control"
                    value={fallbackUrl} readOnly
                    style={{ fontFamily: 'monospace', fontSize: 12, background: '#fffbeb', color: '#92400e' }} />
                  <button className="btn btn-outline-warning" onClick={() => copyLink(fallbackUrl, 'fallback')} style={{ minWidth: 90 }}>
                    {copiedKey === 'fallback'
                      ? <><i className="bi bi-check-lg me-1"></i>Copied!</>
                      : <><i className="bi bi-clipboard me-1"></i>Copy</>}
                  </button>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                  <a href={fallbackUrl} target="_blank" rel="noreferrer" className="btn btn-outline-warning btn-sm">
                    <i className="bi bi-box-arrow-up-right me-1"></i>Preview
                  </a>
                  <a href={`mailto:?subject=Login to ${form.name || 'your workspace'}&body=Please use this link to log in:%0A${fallbackUrl}`}
                    className="btn btn-outline-secondary btn-sm">
                    <i className="bi bi-envelope me-1"></i>Send via Email
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right column */}
          <div className="col-lg-4">
            {/* How to share */}
            <div className="card mb-3">
              <div className="card-header fw-semibold">
                <i className="bi bi-info-circle me-2 text-primary"></i>Which URL to Share?
              </div>
              <div className="card-body p-0">
                <ul className="list-group list-group-flush" style={{ fontSize: 12.5 }}>
                  {[
                    { icon: 'bi-check-circle-fill', color: '#22c55e', text: <><strong>Subdomain URL</strong> — Send this to clients. Looks professional, hides internal identifiers.</> },
                    { icon: 'bi-dash-circle-fill', color: '#94a3b8', text: <><strong>Slug</strong> — Only share internally with developers or support staff.</> },
                    { icon: 'bi-exclamation-circle-fill', color: '#f59e0b', text: <><strong>Fallback URL</strong> — Use only as backup when subdomain isn't ready.</> },
                  ].map((tip, i) => (
                    <li className="list-group-item d-flex gap-2 align-items-start" key={i}>
                      <i className={`bi ${tip.icon} mt-1`} style={{ fontSize: 13, flexShrink: 0, color: tip.color }}></i>
                      <span>{tip.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* How users log in */}
            <div className="card mb-3">
              <div className="card-header fw-semibold">
                <i className="bi bi-person-check me-2 text-primary"></i>How Users Log In
              </div>
              <div className="card-body" style={{ fontSize: 13 }}>
                <ol className="ps-3 mb-0" style={{ lineHeight: 2.3, color: '#334155' }}>
                  <li>Open the <strong>Subdomain URL</strong></li>
                  <li>Organization is <strong>auto-detected</strong></li>
                  <li>Enter <strong>email &amp; password</strong></li>
                  <li>Click <strong>Sign In</strong></li>
                </ol>
              </div>
            </div>

            {/* Security Tips */}
            <div className="card">
              <div className="card-header fw-semibold">
                <i className="bi bi-shield-lock me-2 text-primary"></i>Security Tips
              </div>
              <div className="card-body p-0">
                <ul className="list-group list-group-flush" style={{ fontSize: 12 }}>
                  {[
                    'Use strong passwords for all user accounts',
                    'Enable 2FA for admin accounts (coming soon)',
                    'Revoke access immediately when staff leave',
                    'Review active sessions periodically',
                  ].map((tip, i) => (
                    <li className="list-group-item d-flex gap-2 align-items-start" key={i}>
                      <i className="bi bi-check-circle-fill text-success mt-1" style={{ fontSize: 11, flexShrink: 0 }}></i>
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Domain Tab */}
      {activeTab === 'domain' && (
        <div className="row g-4">
          <div className="col-lg-8">
            {/* Subdomain */}
            <div className="card mb-3">
              <div className="card-header fw-semibold"><i className="bi bi-link-45deg me-2 text-primary"></i>Platform Subdomain</div>
              <div className="card-body">
                <p style={{ fontSize: 13, color: '#64748b' }}>
                  Your organization has a subdomain on our platform. Share this URL with your users — it's always available even if you set up a custom domain.
                </p>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <code style={{ fontSize: 14, background: '#f1f5f9', padding: '6px 12px', borderRadius: 8, border: '1px solid #e2e8f0', flexGrow: 1 }}>
                    https://{domainInfo.subdomain || tenant?.slug}.yourapp.com
                  </code>
                  <button className="btn btn-outline-secondary btn-sm" onClick={() => navigator.clipboard.writeText(`https://${domainInfo.subdomain || tenant?.slug}.yourapp.com`)}>
                    <i className="bi bi-clipboard"></i>
                  </button>
                </div>
                <form onSubmit={saveDomain}>
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Custom Subdomain <span className="text-muted" style={{ fontWeight: 400 }}>(optional)</span></label>
                  <div className="input-group">
                    <input className="form-control" value={domainInfo.subdomain}
                      onChange={e => setDomainInfo(d => ({ ...d, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                      placeholder={tenant?.slug || 'your-org-name'} />
                    <span className="input-group-text">.yourapp.com</span>
                  </div>
                  <div className="form-text">Only lowercase letters, numbers, and hyphens. Default is your org slug: <code>{tenant?.slug}</code></div>
                  <button type="submit" className="btn btn-primary btn-sm mt-2" disabled={domainSaving}>
                    {domainSaving ? <span className="spinner-border spinner-border-sm me-1"></span> : null}
                    Save Subdomain
                  </button>
                </form>
              </div>
            </div>

            {/* Custom Domain */}
            <div className="card mb-3">
              <div className="card-header fw-semibold d-flex justify-content-between align-items-center">
                <span><i className="bi bi-globe2 me-2 text-success"></i>Custom Domain</span>
                {domainInfo.customDomain && (
                  <span className={`badge ${domainInfo.customDomainVerified ? 'bg-success' : 'bg-warning text-dark'}`}>
                    {domainInfo.customDomainVerified ? <><i className="bi bi-check-circle me-1"></i>Verified</> : <><i className="bi bi-clock me-1"></i>Pending Verification</>}
                  </span>
                )}
              </div>
              <div className="card-body">
                <p style={{ fontSize: 13, color: '#64748b' }}>
                  Use your own domain (e.g. <code>app.yourschool.com</code>) so your users see your brand — not ours.
                </p>
                <form onSubmit={saveDomain}>
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Your Custom Domain</label>
                  <div className="input-group mb-2">
                    <span className="input-group-text"><i className="bi bi-globe2"></i></span>
                    <input className="form-control" value={domainInfo.customDomain}
                      onChange={e => setDomainInfo(d => ({ ...d, customDomain: e.target.value.toLowerCase().trim() }))}
                      placeholder="app.yourschool.com" />
                    {domainInfo.customDomain && !domainInfo.customDomainVerified && (
                      <button type="button" className="btn btn-outline-success" onClick={verifyDomain} disabled={domainVerifying}>
                        {domainVerifying ? <span className="spinner-border spinner-border-sm"></span> : <><i className="bi bi-patch-check me-1"></i>Verify Now</>}
                      </button>
                    )}
                  </div>
                  {domainInfo.customDomainVerified && domainInfo.customDomainVerifiedAt && (
                    <div className="alert alert-success py-2 mb-2" style={{ fontSize: 12 }}>
                      <i className="bi bi-check-circle-fill me-1"></i>
                      Verified on {new Date(domainInfo.customDomainVerifiedAt).toLocaleDateString()}. SSL is active.
                    </div>
                  )}
                  <div className="d-flex gap-2">
                    <button type="submit" className="btn btn-primary btn-sm" disabled={domainSaving}>
                      {domainSaving ? <span className="spinner-border spinner-border-sm me-1"></span> : null}
                      Save Domain
                    </button>
                    {domainInfo.customDomain && (
                      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setDomainInfo(d => ({ ...d, customDomain: '' }))}>
                        Remove
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {/* DNS Setup Instructions */}
            {domainInfo.customDomain && !domainInfo.customDomainVerified && (
              <div className="card border-warning mb-3">
                <div className="card-header fw-semibold" style={{ background: '#fffbeb' }}>
                  <i className="bi bi-diagram-3 me-2 text-warning"></i>DNS Setup Instructions
                </div>
                <div className="card-body">
                  <p style={{ fontSize: 13 }}>Login to your domain registrar (GoDaddy, Namecheap, Cloudflare, etc.) and add the following DNS record:</p>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered" style={{ fontSize: 13 }}>
                      <thead className="table-light"><tr><th>Type</th><th>Name / Host</th><th>Value / Points to</th><th>TTL</th></tr></thead>
                      <tbody>
                        <tr>
                          <td><code>CNAME</code></td>
                          <td><code>{domainInfo.customDomain.split('.').slice(0, -2).join('.') || '@'}</code></td>
                          <td><code>{window.location.hostname}</code></td>
                          <td><code>3600</code></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="alert alert-info py-2" style={{ fontSize: 12 }}>
                    <i className="bi bi-clock me-1"></i>
                    DNS changes can take <strong>24-48 hours</strong> to propagate. Once done, click "Verify Now" above.
                  </div>
                  <div className="alert alert-secondary py-2" style={{ fontSize: 12 }}>
                    <i className="bi bi-shield-check me-1"></i>
                    <strong>SSL/HTTPS</strong> is automatically provisioned once your domain is verified. No extra setup needed.
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right: How it works */}
          <div className="col-lg-4">
            <div className="card" style={{ borderRadius: 12 }}>
              <div className="card-header fw-semibold" style={{ fontSize: 13 }}>
                <i className="bi bi-info-circle me-2 text-primary"></i>How Domain Routing Works
              </div>
              <div className="card-body p-3">
                <div className="d-flex flex-column gap-3">
                  {[
                    { icon: 'bi-globe2', color: '#22c55e', title: 'Custom Domain (Priority 1)', desc: `app.yourschool.com → your workspace`, badge: domainInfo.customDomainVerified ? 'Active' : 'Setup needed', badgeColor: domainInfo.customDomainVerified ? 'success' : 'secondary' },
                    { icon: 'bi-link-45deg', color: '#6366f1', title: 'Subdomain (Priority 2)', desc: `${domainInfo.subdomain || tenant?.slug}.yourapp.com`, badge: 'Always available', badgeColor: 'primary' },
                    { icon: 'bi-tag', color: '#f59e0b', title: 'Direct Login Link (Fallback)', desc: `yourapp.com/login?org=${tenant?.slug}`, badge: 'Default', badgeColor: 'warning' },
                  ].map((item, i) => (
                    <div key={i} className="d-flex gap-3 align-items-start">
                      <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{ width: 36, height: 36, background: `${item.color}15` }}>
                        <i className={`bi ${item.icon}`} style={{ color: item.color, fontSize: 16 }}></i>
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{item.title}</div>
                        <code style={{ fontSize: 11, color: '#64748b' }}>{item.desc}</code>
                        <div><span className={`badge bg-${item.badgeColor} bg-opacity-10 text-${item.badgeColor}`} style={{ fontSize: 10 }}>{item.badge}</span></div>
                      </div>
                    </div>
                  ))}
                </div>
                <hr />
                <div style={{ fontSize: 12, color: '#64748b' }}>
                  <i className="bi bi-shield-check me-1 text-success"></i>
                  All three access methods are always active simultaneously. Your users can use any of them.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <AlertToast {...toast} onClose={() => setToast(t => ({ ...t, show: false }))} />

      {['super_admin', 'admin'].includes(user?.roleKey) && (
        <button
          onClick={() => navigate('/dm')}
          style={{
            position: 'fixed',
            bottom: 8,
            right: 16,
            fontSize: '0.65rem',
            color: '#999',
            background: 'none',
            border: '1px solid #ddd',
            borderRadius: '4px',
            padding: '2px 6px',
            cursor: 'pointer',
            fontFamily: 'monospace',
            zIndex: 9999,
          }}
          title="Data Migration (Admin Only)"
        >
          [dm]
        </button>
      )}
    </div>
  );
}
